import React, { useMemo, useRef } from 'react';
import {
  getIconForCategory,
  getImageUrl,
  formatPrice,
  parseProductKey,
  sortCategoriesWithConfig,
  cleanProductName
} from './AppUtils.js';
import { isAdminMode, isLockedProduct } from './constants.js';

// ============================================================================
// PERFORMANCE OPTIMIZATION: Memoized category ordering
// ============================================================================
const useOrderedCategories = (selectedProducts, categoryConfig) => {
  return useMemo(() => {
    // Get all categories that have selected products
    const categoriesWithProducts = new Set();
    Object.entries(selectedProducts).forEach(([productKey]) => {
      const { mainCategory } = parseProductKey(productKey);
      categoriesWithProducts.add(mainCategory);
    });

    const categoriesArray = Array.from(categoriesWithProducts);
    
    // EXPLICIT: Chassis always first, regardless of any other logic
    const orderedList = [];
    
    // 1. Add Chassis first if it has products
    if (categoriesArray.includes('Chassis')) {
      orderedList.push('Chassis');
    }
    
    // 2. Add all other categories using the sorting function
    const nonChassisCategories = categoriesArray.filter(cat => cat.toLowerCase() !== 'chassis');
    const sortedNonChassis = sortCategoriesWithConfig(nonChassisCategories, categoryConfig || {});
    orderedList.push(...sortedNonChassis);
    
    return orderedList;
  }, [selectedProducts, categoryConfig]);
};

// ============================================================================
// PERFORMANCE OPTIMIZATION: Memoized product grouping
// ============================================================================
const useProductsByCategory = (selectedProducts, orderedCategories) => {
  return useMemo(() => {
    const grouped = {};
    
    // Initialize all relevant categories
    orderedCategories.forEach(category => {
      grouped[category] = [];
    });
    
    // Add selected products
    const productEntries = Object.entries(selectedProducts);
    
    productEntries.forEach(([productKey, product]) => {
      const { mainCategory } = parseProductKey(productKey);
      if (grouped[mainCategory]) {
        grouped[mainCategory].push({ productKey, product });
      } else {
        // Handle case where product category isn't in orderedCategories
        grouped[mainCategory] = [{ productKey, product }];
      }
    });

    // Sort products within each category by subcategory and then by name
    Object.keys(grouped).forEach(category => {
      grouped[category].sort((a, b) => {
        const { subCategory: subA } = parseProductKey(a.productKey);
        const { subCategory: subB } = parseProductKey(b.productKey);
        
        // First sort by subcategory
        if (subA !== subB) {
          return subA.localeCompare(subB);
        }
        
        // Then by product name
        return a.product.name.localeCompare(b.product.name);
      });
    });

    return grouped;
  }, [selectedProducts, orderedCategories]);
};

// Build Summary Component - ENHANCED FOR RESPONSIVE BEHAVIOR
const BuildSummary = ({
  selectedProducts,
  mainCategories,
  categoryConfig = {},
  totalPrice,
  totalTestPrice,
  onRemoveProduct,
  onNavigateToCategory,
  onShareBuild,
  onContactSales,
  compact = false,
  hideTotal = false
}) => {
  const scrollRef = useRef(null);

  // PERFORMANCE: Use memoized hooks instead of inline useMemo
  const orderedCategories = useOrderedCategories(selectedProducts, categoryConfig);
  const productsByCategory = useProductsByCategory(selectedProducts, orderedCategories);

  const handleProductClick = (productKey, product, mainCategory, subCategory) => {
    const isLocked = product.isLocked || isLockedProduct(product, mainCategory, subCategory);
    
    if (isLocked) {
      // For locked products, trigger the remove handler which will show the locked popup
      onRemoveProduct(productKey);
    } else {
      // For unlocked products, navigate to the category
      onNavigateToCategory(mainCategory, subCategory, product);
    }
  };

  const handleRemoveProduct = (productKey) => {
    // This will call the parent's handleRemoveProduct which shows the locked popup
    onRemoveProduct(productKey);
  };

  return (
    <div className="flex flex-col bg-[#707070] h-full max-h-full overflow-hidden relative">
      {/* Header - Fixed at top with flex-shrink-0 */}
      <div className={`${compact ? 'px-2 py-1' : 'px-3 py-2'} border-b border-white/15 flex items-center justify-between bg-black flex-shrink-0`}>
        <h3 className={`${compact ? 'text-xs' : 'text-sm lg:text-base'} font-bold text-white uppercase`}>Your Build</h3>
        <div className="flex items-center gap-2">
          <div className={`${compact ? 'text-[9px]' : 'text-xs lg:text-sm'} text-white/80`}>
            {Object.keys(selectedProducts).length} items
          </div>
        </div>
      </div>

      {/* Categories List - Scrollable middle section with proper flex properties */}
      <div 
        ref={scrollRef}
        className="flex-1 bg-transparent overflow-y-auto min-h-0 max-h-full dark-scroll-stylish"
      >
        <div className={`${compact ? 'space-y-1' : 'space-y-1'} h-full`}>
          {orderedCategories.map((categoryName) => {
            const categoryProducts = productsByCategory[categoryName] || [];
            const hasProducts = categoryProducts.length > 0;
            
            // Only show categories that have products
            if (!hasProducts) return null;
            
            return (
              <div key={categoryName} className="bg-[#E0E0E0] mx-1 rounded-md overflow-hidden">
                {/* Category Header - Always Visible */}
                <div className={`flex items-center justify-center ${compact ? 'py-1 px-2' : 'py-1.5 px-3'} bg-[#1a1a1a]`}>
                  <span className={`font-bold ${compact ? 'text-[9px]' : 'text-xs'} text-white uppercase leading-none`}>
                    {categoryName}
                  </span>
                </div>
                
                {/* Category Content - Always Expanded */}
                <div className={`${compact ? 'p-1' : 'p-1'}`}>
                  <div className={`${compact ? 'space-y-0.5' : 'space-y-0.5'}`}>
                    {categoryProducts.map(({ productKey, product }) => {
                      // FIXED: Clean parsing of productKey to avoid weird characters in subcategory
                      let mainCategory, subCategory;
                      
                      if (productKey.includes('_')) {
                        // This is a multi-select wheel rim key like "Racing Controls||Wheel Rim_productId123"
                        const baseKey = productKey.split('_')[0]; // Remove the _productId part
                        const parsed = parseProductKey(baseKey);
                        mainCategory = parsed.mainCategory;
                        subCategory = parsed.subCategory;
                      } else {
                        // Regular single-select key
                        const parsed = parseProductKey(productKey);
                        mainCategory = parsed.mainCategory;
                        subCategory = parsed.subCategory;
                      }
                      
                      const isLocked = product.isLocked || isLockedProduct(product, mainCategory, subCategory);
                      
                      return (
                        <div key={productKey} className="relative group">
                          {/* Clickable Product Box */}
                          <div
                            onClick={() => handleProductClick(productKey, product, mainCategory, subCategory)}
                            className={`flex items-center gap-2 ${compact ? 'p-1' : 'p-1.5'} bg-gradient-to-r from-white/60 via-gray-50/60 to-white/60 rounded cursor-pointer hover:bg-gray-50 transition-all duration-200 ${
                              isLocked ? 'bg-gray-50 border-gray-300' : ''
                            }`}
                          >
                            <div className={`${compact ? 'text-[10px]' : 'text-sm'} flex-shrink-0`}>
                              {(() => {
                                const imageUrl = getImageUrl(product.image);
                                
                                return imageUrl ? (
                                  <div className={`${compact ? 'w-6 h-6' : 'w-10 h-10'} bg-gray-100 rounded overflow-hidden`}>
                                    <img 
                                      src={imageUrl}
                                      alt={product.name}
                                      loading="lazy"
                                      className="w-full h-full object-cover"
                                      onError={(e) => {
                                        e.target.style.display = 'none';
                                        e.target.parentElement.querySelector('.thumbnail-fallback').style.display = 'flex';
                                      }}
                                    />
                                    <div className={`thumbnail-fallback w-full h-full items-center justify-center text-black ${compact ? 'text-xs' : 'text-base'}`} style={{ display: 'none' }}>
                                      {getIconForCategory(subCategory, categoryConfig)}
                                    </div>
                                  </div>
                                ) : (
                                  <div className={`${compact ? 'w-6 h-6' : 'w-10 h-10'} bg-gray-100 rounded flex items-center justify-center text-black ${compact ? 'text-xs' : 'text-base'}`}>
                                    {getIconForCategory(subCategory, categoryConfig)}
                                  </div>
                                );
                              })()}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className={`text-black uppercase ${compact ? 'text-[7px]' : 'text-[9px]'}`}>
                                {subCategory}
                              </div>
                              <div className={`truncate ${compact ? 'text-[8px]' : 'text-xs'} ${
                                isLocked ? 'text-black' : 'text-black'
                              }`}>
                                {(() => {
                                  // Just use the clean product name - ignore all the key mess
                                  let cleanName = cleanProductName(product.name);

                                  // For multi-select wheel rims, add numbering if we have duplicates
                                  if (subCategory === 'Wheel Rim') {
                                    // Count how many wheel rims of this exact CLEAN product name we have
                                    const wheelRimProducts = categoryProducts.filter(({ product: p }) => {
                                      const otherCleanName = cleanProductName(p.name);
                                      return otherCleanName === cleanName;
                                    });

                                    if (wheelRimProducts.length > 1) {
                                      // Find the index of this specific product key
                                      const currentIndex = wheelRimProducts.findIndex(({ productKey: pk }) => pk === productKey);
                                      return `${cleanName} ${currentIndex + 1}`;
                                    }
                                  }

                                  return cleanName;
                                })()}
                              </div>
                            </div>
                            {/* Hide price for locked products (Chassis, Shipping, etc.) unless admin mode */}
                            {!isLockedProduct(product, mainCategory, subCategory) ? (
                              <div className={`text-red-600 flex-shrink-0 self-end ${compact ? 'text-[8px]' : 'text-xs'}`}>
                                {formatPrice(product.price)}
                                {isAdminMode() && product.testPrice !== null && product.testPrice !== undefined && (
                                  <div className="text-blue-600 text-[10px]" title="Test Price">
                                    ({formatPrice(product.testPrice)})
                                  </div>
                                )}
                              </div>
                            ) : (
                              isAdminMode() && (
                                <div className={`text-red-600 flex-shrink-0 self-end ${compact ? 'text-[8px]' : 'text-xs'}`}>
                                  {formatPrice(product.price)}
                                  {product.testPrice !== null && product.testPrice !== undefined && (
                                    <div className="text-blue-600 text-[10px]" title="Test Price">
                                      ({formatPrice(product.testPrice)})
                                    </div>
                                  )}
                                </div>
                              )
                            )}

                            {/* Lock Icon */}
                            {isLocked && (
                              <div className={`flex-shrink-0 ml-0.5 ${compact ? 'text-[8px]' : 'text-xs'}`}>
                                <span className="text-orange-500" title="Pre-selected component">🔒</span>
                              </div>
                            )}
                          </div>
                          
                          {/* Remove Button - Hidden for locked products */}
                          {!isLocked && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRemoveProduct(productKey);
                              }}
                              className={`absolute top-1 right-1 text-red-500 hover:text-red-700 ${compact ? 'text-[7px] w-2.5 h-2.5' : 'text-xs w-4 h-4'} flex items-center justify-center bg-white rounded-full shadow-sm hover:shadow-md transition-all`}
                            >
                              ✕
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      
      {/* Footer with Total and integrated buttons */}
      {!hideTotal && (
        <div className={`${compact ? 'p-2' : 'p-3'} flex-shrink-0 bg-black text-white`}>
          <div className="flex justify-between items-center mb-3">
            <div className={`${compact ? 'text-[9px]' : 'text-sm'} opacity-75 uppercase`}>Total:</div>
            <div className="flex flex-col items-end">
              <div className={`${compact ? 'text-sm' : 'text-lg'} font-bold`}>
                {formatPrice(totalPrice)}
              </div>
              {isAdminMode() && (
                <div className="text-blue-400 text-xs font-medium" title="Test Price Total">
                  {formatPrice(totalTestPrice || 0)}
                </div>
              )}
            </div>
          </div>

          {/* Button row */}
          {(onShareBuild || onContactSales) && (
            <div className="flex gap-2">
              {onShareBuild && (
                <button
                  onClick={onShareBuild}
                  className={`flex-1 bg-white/10 hover:bg-white/20 border border-white/20 text-white ${compact ? 'py-1 px-2' : 'py-1.5 px-2'} font-medium transition-all duration-200 flex items-center justify-center gap-1`}
                  title="Share this build"
                >
                  <svg className={`${compact ? 'w-3 h-3' : 'w-4 h-4'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
                  </svg>
                  <span className={`${compact ? 'text-xs' : 'text-sm'} font-medium hidden sm:inline uppercase`}>Share</span>
                </button>
              )}

              {onContactSales && (
                <button
                  onClick={onContactSales}
                  className={`flex-1 bg-white text-black hover:bg-gray-100 ${compact ? 'py-1 px-2' : 'py-1.5 px-2'} font-medium transition-all duration-200 flex items-center justify-center gap-1`}
                  title="Submit order inquiry"
                >
                  <svg className={`${compact ? 'w-3 h-3' : 'w-4 h-4'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <span className={`${compact ? 'text-xs' : 'text-sm'} font-medium hidden sm:inline uppercase`}>Submit</span>
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BuildSummary;